import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useEffect, useLayoutEffect } from 'react';

import type { GridColDef, GridInitialState } from '@mui/x-data-grid';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import isEmpty from 'lodash/isEmpty';

import { filterPersistedState } from './filter-persisted-state';
import { hideNewFields } from './hide-new-fields';

export const usePersistedGridEffects = ({
  apiRef,
  columns,
  isLoading,
  defaultState,
  featuresToPersist,
  persistedGridState,
  persistedInitialState,
  persistBeforeunload = false,
  setIsLoading,
  setPersistedInitialState,
}: {
  persistedGridState: any;
  isLoading: boolean;
  columns: GridColDef[];
  persistBeforeunload?: boolean;
  apiRef: RefObject<GridApiCommunity>;
  featuresToPersist: Record<string, any>;
  persistedInitialState?: GridInitialState;
  defaultState: GridInitialState;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setPersistedInitialState: Dispatch<
    SetStateAction<GridInitialState | undefined>
  >;
}) => {
  // Handle beforeunload persistence
  useLayoutEffect(() => {
    if (!persistBeforeunload) return;

    const handleBeforeUnload = () => {
      if (isEmpty(apiRef.current)) {
        return;
      }

      if (!persistedInitialState) return;

      const currentState = apiRef.current.exportState();
      const filteredState = filterPersistedState(
        currentState,
        featuresToPersist,
      );

      persistedGridState.set(filteredState);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // this line ensures that the state is saved when following links
      handleBeforeUnload();
    };
  }, [
    apiRef,
    featuresToPersist,
    persistedGridState,
    persistBeforeunload,
    persistedInitialState,
  ]);

  // Fetch persisted state
  useEffect(() => {
    if (persistedInitialState || !isLoading) return;

    const fetchState = async () => {
      const state = await persistedGridState.get(defaultState);

      if (!isEmpty(state)) {
        const filteredState = filterPersistedState(state, featuresToPersist);
        const updatedState = hideNewFields(columns, filteredState, state);

        apiRef.current?.restoreState?.(updatedState);
        persistedGridState.set(updatedState);

        setPersistedInitialState(updatedState);
        setIsLoading(false);
      }
    };
    fetchState();
  }, [
    apiRef,
    columns,
    isLoading,
    defaultState,
    featuresToPersist,
    persistedGridState,
    persistedInitialState,
    setIsLoading,
    setPersistedInitialState,
  ]);
};
